// GAP-BACKEND-081 / SPEC-backend-048 AC-8b (2026-08-08, widened after
// impl-runner's STOP-AND-ASK surfaced the downstream consumers).
//
// `session-display-state.ts`'s `sessionDisplayState()` answers ONE question:
// "what label does this session's tile SHOW right now" (AC-8a). Two other
// call sites were quietly using that same answer as a proxy for a SECOND,
// distinct question — "does the rep need to act on this row" — and a third
// bypassed it altogether. Collapsing both questions into one function is
// what broke when AC-8a's rule changed (project_noshow_display_state's
// "the helper is the only derivation point" claim was wrong in practice).
//
// This file factors the SECOND question into its own primitive so a future
// change to the DISPLAY LABEL never again silently drags queue-membership /
// DIA-cell tone along with it.
//
// Two arms, both legitimate, and DELIBERATELY not collapsed into one status
// check:
//   * Completed + empty attendance   — this SPEC's subject: the emulator (and,
//     once real Play does a host-close, production) now reaches Completed for
//     an empty room, and the attendance fact is the observation.
//   * Scheduled + past start time    — the TRANSITION-WINDOW case: a real
//     empty room that has NOT yet been closed (host hasn't pressed
//     exit-with-complete, or the emulator hasn't populated it) is still a
//     row the rep needs to act on. This arm is [[GAP-BACKEND-082]]'s to
//     retire (a time-based sweeper that closes stale Active/Scheduled rows) —
//     do NOT delete it here. Until 082 ships, this is the only signal that
//     a stale empty room needs the rep's attention at all.
//
// Both arms answer "is this commercially actionable", not "what does the
// tile say" — a Scheduled-past row here is actionable but its DISPLAY label
// (session-display-state.ts) reads `Upcoming`, and that split is intentional
// per AC-8a/AC-8b.

export function isRepActionableSession(meeting: {
    status?: string | null;
    startTime?: string | null;
    scheduledAt?: string | null;
    scheduledStartTime?: string | null;
    attendedCount?: number | null;
    joinEventCount?: number | null;
} | null | undefined, now: Date = new Date()): boolean {
    if (!meeting) { return false; }

    const status = (meeting.status ?? '').toString().toLowerCase();

    if (status === 'completed') {
        const attended = meeting.attendedCount ?? 0;
        const joins = meeting.joinEventCount ?? 0;
        return attended <= 1 && joins === 0;
    }

    if (status === 'scheduled') {
        const start = meeting.startTime ?? meeting.scheduledAt ?? meeting.scheduledStartTime;
        const startDate = start ? new Date(start) : null;
        return !!startDate && !isNaN(startDate.getTime()) && startDate.getTime() < now.getTime();
    }

    return false;
}
